import { isFuture } from 'date-fns';
import { persist } from 'mobx-persist';

import { parkingApi } from '@api/parking.api';
import { SerializedInstant } from '@models/api/data.types';
import {
  ConfirmParkingAgreementResponse,
  ParkingProduct,
} from '@models/parking.types';

import { createOnAuthenticateListener } from '../authStore';

/**
 * A base class for parkingRentingStore, where user can rent a new parking space, and parkingChangingStore, where user can switch their parking space to another.
 * Contains logic for locking, unlocking and aborting the renting process.
 */
export class ParkingStoreBase {
  @persist('object') lockedParkingProduct: ParkingProduct | null = null;
  @persist lockEndDate: SerializedInstant | null = null;

  /** Parking agreement confirmation API response. Contains possible access code for the parking area. */
  @persist('object')
  agreementConfirmationResponse: ConfirmParkingAgreementResponse | null = null;

  /** ID of the created draft agreement in cms */
  @persist draftAgreementId: string | null = null;

  constructor() {
    // Clear state if there is a lock but it is expired
    if (this.lockEndDate && !this.hasOngoingLock) {
      this.clear();
    }

    createOnAuthenticateListener(
      () => {},
      () => {
        if (this.hasOngoingLock) {
          // Unlock the currently locked product if user logs out while they have a locked product
          parkingApi.unlockParkingProduct(this.lockedParkingProduct!.id);
        }

        this.clear();
      }
    );
  }

  get hasOngoingLock() {
    return this.lockEndDate && this.lockedParkingProduct
      ? isFuture(new Date(this.lockEndDate))
      : false;
  }

  /** Gets if product with a given id is currently locked by this user */
  getIsProductIdCurrentlyLocked(id: string) {
    return this.hasOngoingLock && this.lockedParkingProduct?.id === id;
  }

  protected async lockProduct(parkingProduct: ParkingProduct) {
    const lockResult = await parkingApi.lockParkingProduct(parkingProduct.id);

    this.lockEndDate = lockResult.data.lockEnds;
    this.lockedParkingProduct = parkingProduct;
  }

  /** Aborts parking product rent process */
  async abortRentingAndUnlockProduct() {
    if (!this.lockedParkingProduct || !this.hasOngoingLock) return;

    if (this.draftAgreementId) {
      // If a draft agreement was created in CMS, it has to be cancelled
      await parkingApi.cancelParkingProductRenting(this.draftAgreementId);
    } else {
      // Else just unlock the product
      await parkingApi.unlockParkingProduct(this.lockedParkingProduct.id);
    }
  }

  clear() {
    // Clear store values
    this.lockedParkingProduct = null;
    this.lockEndDate = null;
    this.draftAgreementId = null;
    this.agreementConfirmationResponse = null;
  }
}
